import { TextLink } from '@domgen/base-design-system-2.0/atoms/TextLink';
import { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { ConsentBox } from '../hooks/useConsentBoxes';

const styles = {
  heading: 'text-h4',
  label: 'flex flex-col gap-space-200 text-body',
  details:
    'flex flex-col gap-space-400 mt-space-400 pt-space-400 border-t border-neutral-500 text-body',
};

const PolicyLink = ({ href, children }: { href?: string; children: ReactNode }) =>
  href ? (
    <TextLink href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </TextLink>
  ) : (
    children
  );

/**
 * @description Content of one SDK consent box in the checkout test variant, rendered into the slots
 * prepared by useConsentBoxes: heading, checkbox label, and the payment and cover details.
 */
export const CheckoutConsent = ({
  consentBox,
  paymentStartDate,
}: {
  consentBox: ConsentBox;
  paymentStartDate?: string;
}) => (
  <>
    {createPortal(<h4 className={styles.heading}>Review and confirm</h4>, consentBox.headingSlot)}
    {createPortal(
      <label htmlFor={consentBox.checkboxId} className={styles.label}>
        <span>
          I agree to the automatic renewal of this plan and authorize Domestic & General to charge
          my selected payment method monthly until the plan is cancelled.
        </span>
        <span>
          I acknowledge the{' '}
          <PolicyLink href={consentBox.brandPrivacyUrl}>
            {import.meta.env.VITE_CLIENT_FRIENDLY_NAME}
          </PolicyLink>{' '}
          & <PolicyLink href={consentBox.dgPrivacyUrl}>{'D&G'}</PolicyLink> privacy policies.
        </span>
      </label>,
      consentBox.labelSlot,
    )}
    {createPortal(
      <div className={styles.details}>
        {paymentStartDate && (
          <p>
            Your first monthly payment will be taken on {paymentStartDate} and your protection plan
            will start from that date. Future payments will then be taken on that same day of every
            month.
          </p>
        )}
        <p>
          You have a 30-day free-look period. If you cancel your plan within this period and have
          not made a claim, you will receive a full refund.
        </p>
        <p>
          If your appliance is out of warranty then a 30 day wait period applies before you can make
          a claim.
        </p>
      </div>,
      consentBox.detailsSlot,
    )}
  </>
);
